import { Component, ComponentInterface, Element, h, Host, Prop, Watch } from '@stencil/core';
import { CountUp } from 'countup.js';

interface UiCmsSupernumberBody {
  size: 'sm' | 'md' | 'lg';
  number: number;
  numberPrefix?: string;
  numberPostfix?: string;
  numberAnimated?: boolean;
  animationStartValue?: number;
  animationDecimalPlaces?: number;
  animationDuration?: number;
  omitSpaceAfterNumber?: boolean;
  overline?: string;
  description?: string;
  subline?: string;
  excludeSeparators?: boolean;
}

type ComponentNames = 'SD-ACCORDION' | 'SD-TAB-PANEL';

@Component({
  tag: 'ui-cms-supernumber',
  styleUrl: 'ui-cms-supernumber.scss',
})
export class UiCmsSupernumber implements ComponentInterface {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsSupernumberBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Prop() inverted: boolean;

  private numberRef: HTMLElement;

  private startNumber: number;

  private prefix: string;

  private postfix: string;

  private countUp: CountUp;

  /** only needed when animation is enabled */
  private animationTriggerComponentNames: ComponentNames[] = ['SD-ACCORDION', 'SD-TAB-PANEL'];

  /** only needed when animation is enabled and element is inside an animation trigger component */
  private animationTriggerElement: any;

  private calculateStartNumber(number: number): number {
    return number > 50 ? Math.floor(number - 50) : 0;
  }

  private isPlacedInside(componentNames: ComponentNames[]): HTMLElement | null {
    const selector = componentNames.map((name) => name.toLowerCase()).join(', ');

    return this.el.closest(selector);
  }

  private async initAnimation(): Promise<void> {
    const { number, animationDuration } = this.body;
    this.animationTriggerElement = this.isPlacedInside(this.animationTriggerComponentNames);

    let isVisible = true;

    if (this.animationTriggerElement) {
      await this.animationTriggerElement.updateComplete;

      if (this.animationTriggerElement.tagName === 'SD-ACCORDION') {
        this.accordionShowHandler();
        isVisible = this.animationTriggerElement.open;
      }
      if (this.animationTriggerElement.tagName === 'SD-TAB-PANEL') {
        this.tabShowHandler();
        // Without the timeout, the active prop is not set on the panel before execution
        await new Promise<void>((resolve) => {
          setTimeout(() => {
            isVisible = this.animationTriggerElement.active;
            resolve();
          }, 500);
        });
      }
    }

    const { decimal, thousands } = this.getSeparators();
    const config = {
      prefix: this.prefix,
      suffix: this.postfix,
      startVal: this.startNumber,
      enableScrollSpy: isVisible,
      scrollSpyOnce: isVisible,
      separator: thousands,
      decimal,
      duration: animationDuration / 1000 || 0.6,
      decimalPlaces: this.body.animationDecimalPlaces || 0,
    };

    this.countUp = new CountUp(this.numberRef, number, config);
    setTimeout(() => this.countUp.handleScroll(this.countUp), 500);
  }

  private getSeparators(): { decimal: string; thousands: string } {
    if (this.body.excludeSeparators) {
      return { decimal: '', thousands: '' };
    }

    const numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'de-DE');
    const formatted = numberFormat.format(12345.6789);
    let decimalSeparator = '';
    let thousandsSeparator = '';

    for (let i = 0, n = formatted.length; i < n; i += 1) {
      if (!'0123456789'.includes(formatted[i])) {
        if (thousandsSeparator) {
          decimalSeparator = formatted[i];
          break;
        } else {
          thousandsSeparator = formatted[i];
        }
      }
    }

    return { decimal: decimalSeparator, thousands: thousandsSeparator };
  }

  private accordionShowHandler(): void {
    this.animationTriggerElement.addEventListener('sd-after-show', () => {
      if ((this.animationTriggerElement as any).open) {
        this.countUp.start();
      }
    });
  }

  private tabShowHandler(): void {
    this.animationTriggerElement.closest('sd-tab-group').addEventListener('sd-tab-show', (e: CustomEvent) => {
      if (e.detail.name === (this.animationTriggerElement as any).name) {
        this.countUp.start();
      }
    });
  }

  private cleanupEventListeners(): void {
    if (this.animationTriggerElement.tagName === 'SD-ACCORDION') {
      this.animationTriggerElement.removeEventListener('sd-after-show', this.accordionShowHandler);
    }
    if (this.animationTriggerElement.tagName === 'SD-TAB-PANEL') {
      this.animationTriggerElement.closest('sd-tab-group').removeEventListener('sd-tab-show', this.tabShowHandler);
    }
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.startNumber = this.body.animationStartValue ?? this.calculateStartNumber(this.body.number);
    this.prefix = `${this.body.numberPrefix ? this.body.numberPrefix.replace(/&nbsp;/g, ' ').trimEnd() : ''} `;
    this.postfix = `${this.body.omitSpaceAfterNumber ? '' : ' '}${this.body.numberPostfix ? this.body.numberPostfix.replace(/&nbsp;/g, ' ').trimStart() : ''}`;
  }

  componentDidLoad() {
    if (this.body.numberAnimated) {
      this.initAnimation();
    }
  }

  disconnectedCallback() {
    if (this.animationTriggerElement) {
      this.cleanupEventListeners();
    }
  }

  // Dev Note: text content has to be set via innerHTML to allow for HTML content.
  // HTML content is needed because of sup and sub.
  render() {
    const { overline, subline, description, number } = this.body;
    const invertedClass = (className: string) => (this.inverted ? `${className}--inverted` : '');
    const sizeClass = `supernumber--${this.body.size || 'md'}`;

    return (
      <Host>
        <div class={`supernumber ${sizeClass}`}>
          {overline && (
            <p class={`supernumber__overline sd-paragraph ${invertedClass('sd-paragraph')}`} innerHTML={overline}></p>
          )}
          <div
            ref={(el) => {
              this.numberRef = el;
            }}
            class={`supernumber__number ${invertedClass('supernumber__number')}`}
          >
            {this.prefix && <span innerHTML={this.prefix}></span>}
            {number}
            {this.postfix && <span innerHTML={this.postfix}></span>}
          </div>
          {subline && (
            <p class={`supernumber__subline sd-paragraph ${invertedClass('sd-paragraph')}`} innerHTML={subline}></p>
          )}
          {description && (
            <p
              class={`supernumber__description sd-pararaph sd-paragraph--size-sm ${invertedClass('sd-paragraph')}`}
              innerHTML={description}
            ></p>
          )}
        </div>
      </Host>
    );
  }
}
